import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useAdmin } from './AdminContext';
import { breaksApi } from '../services/cloudApi';
import { normalizeTimestamp } from '../utils/timestampUtils';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import i18n from '../i18n';

// UUID v4 generator
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const BreakReminderContext = createContext(null);

const normalizeBreakRecord = (breakItem) => ({
  id: breakItem.id,
  name: breakItem.name || i18n.t('breaks:defaults.break'),
  startTime: breakItem.startTime ?? breakItem.start_time ?? '10:00',
  durationMinutes: breakItem.durationMinutes ?? breakItem.duration_minutes ?? 15,
  activeDays:
    breakItem.activeDays ??
    (typeof breakItem.active_days === 'string'
      ? JSON.parse(breakItem.active_days || '{}')
      : breakItem.active_days) ?? {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    },
  soundEnabled:
    breakItem.soundEnabled !== undefined
      ? breakItem.soundEnabled
      : breakItem.sound_enabled !== undefined
      ? Boolean(breakItem.sound_enabled)
      : true,
  createdAt: normalizeTimestamp(breakItem.createdAt ?? breakItem.created_at),
  updatedAt: normalizeTimestamp(breakItem.updatedAt ?? breakItem.updated_at ?? breakItem.createdAt ?? breakItem.created_at),
});

export const BreakReminderProvider = ({ children }) => {
  const [breaks, setBreaks] = useState([]);
  const [selectedBreak, setSelectedBreak] = useState(null);
  const { user, isAuthenticated, authToken } = useAuth();
  const { getActiveUserId, getActiveUser } = useAdmin();
  
  // Impersonate durumunda impersonate user'ı, değilse normal user'ı kullan
  const activeUserId = getActiveUserId ? getActiveUserId() : user?.id;
  const activeUser = getActiveUser ? getActiveUser() : user;
  const activeToken = authToken;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleRemote = async (event) => {
      const { detail } = event || {};
      if (detail?.entity !== 'breaks' || !Array.isArray(detail.records) || !detail.records.length) {
        return;
      }
      const normalized = detail.records.map(normalizeBreakRecord);
      
      setBreaks((prev) => {
        // Eğer önceki veri yoksa, sadece yeni gelen verileri döndür
        if (!prev || prev.length === 0) {
          return normalized.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
        }
        
        const map = new Map();
        // Önce mevcut verileri ekle - MEVCUT VERİLERİ KORU
        prev.forEach((item) => map.set(item.id, item));
        let hasChanges = false;
        
        normalized.forEach((item) => {
          const existing = map.get(item.id);
          if (!existing) {
            // Yeni kayıt, ekle
            map.set(item.id, item);
            hasChanges = true;
            return;
          }
          
          // CRITICAL: Check if local record is deleted (deleted_at takes priority)
          const existingDeleted = existing.deletedAt || existing.deleted_at;
          const existingDeletedTs = existingDeleted ? 
            (typeof existingDeleted === 'number' ? existingDeleted : new Date(existingDeleted).getTime() / 1000) : 0;

          // RULE 1: If local record is deleted, keep it (pending delete will be pushed)
          if (existingDeletedTs > 0) {
            // Local record is deleted - keep it, don't accept server record
            return;
          }

          // RULE 2: Timestamp comparison for updates
          const existingTs = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
          const incomingTs = new Date(item.updatedAt || item.createdAt || 0).getTime();
          // Sadece yeni veri daha yeni ise güncelle
          if (incomingTs > existingTs) {
            // Sadece gerçekten farklı veri varsa güncelle
            const existingStr = JSON.stringify(existing);
            const incomingStr = JSON.stringify(item);
            if (existingStr !== incomingStr) {
              map.set(item.id, item);
              hasChanges = true;
            }
          }
          // Eğer eşit veya eski ise, mevcut veriyi koru (değişiklik yok)
        });
        
        // Değişiklik yoksa önceki state'i döndür (re-render'ı önle)
        if (!hasChanges) {
          return prev;
        }
        
        // Tüm verileri koruyarak sırala
        return Array.from(map.values()).sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
      });
    };

    const handleOperation = async (event) => {
      const { detail } = event || {};
      const operation = detail?.operation;
      const result = detail?.result;
      if (!operation || operation.entity !== 'breaks') {
        return;
      }

      if (operation.action === 'delete' && operation.payload?.id) {
        const deleteId = operation.payload.id;
        setBreaks((prev) => {
          const exists = prev.some((item) => item.id === deleteId);
          if (!exists) {
            return prev; // Zaten silinmiş, re-render'ı önle
          }
          return prev.filter((breakItem) => breakItem.id !== deleteId);
        });
        setSelectedBreak((prev) => {
          if (prev?.id === deleteId) {
            return null;
          }
          return prev;
        });
        return;
      }

      if (result?.breaks || result?.break) {
        const normalized = (result.breaks || [result.break]).map(normalizeBreakRecord);
        
        setBreaks((prev) => {
          // Eğer önceki veri yoksa, sadece yeni gelen verileri döndür
          if (!prev || prev.length === 0) {
            return normalized.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
          }
          
          const map = new Map();
          // Önce mevcut verileri ekle - MEVCUT VERİLERİ KORU
          prev.forEach((item) => map.set(item.id, item));
          let hasChanges = false;
          
          normalized.forEach((item) => {
            const existing = map.get(item.id);
            if (!existing) {
              // Yeni kayıt, ekle
              map.set(item.id, item);
              hasChanges = true;
              return;
            }
            
            // CRITICAL: Check if local record is deleted (deleted_at takes priority)
            const existingDeleted = existing.deletedAt || existing.deleted_at;
            const existingDeletedTs = existingDeleted ? 
              (typeof existingDeleted === 'number' ? existingDeleted : new Date(existingDeleted).getTime() / 1000) : 0;

            // RULE 1: If local record is deleted, keep it (pending delete will be pushed)
            if (existingDeletedTs > 0) {
              // Local record is deleted - keep it, don't accept server record
              return;
            }

            // RULE 2: Timestamp comparison for updates
            const existingTs = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
            const incomingTs = new Date(item.updatedAt || item.createdAt || 0).getTime();
            // Sadece yeni veri daha yeni ise güncelle
            if (incomingTs > existingTs) {
              // Sadece gerçekten farklı veri varsa güncelle
              const existingStr = JSON.stringify(existing);
              const incomingStr = JSON.stringify(item);
              if (existingStr !== incomingStr) {
                map.set(item.id, item);
                hasChanges = true;
              }
            }
            // Eğer eşit veya eski ise, mevcut veriyi koru (değişiklik yok)
          });
          
          // Değişiklik yoksa önceki state'i döndür (re-render'ı önle)
          if (!hasChanges) {
            return prev;
          }
          
          // Tüm verileri koruyarak sırala
          return Array.from(map.values()).sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
        });
      }
    };

    window.addEventListener('notesplus-sync-remote', handleRemote);
    window.addEventListener('notesplus-sync-operation', handleOperation);

    return () => {
      window.removeEventListener('notesplus-sync-remote', handleRemote);
      window.removeEventListener('notesplus-sync-operation', handleOperation);
    };
  }, [isAuthenticated, activeUserId]);

  // İlk yükleme flag'i - sadece bir kez çalışsın
  const hasLoadedRef = React.useRef(false);
  const lastUserIdRef = React.useRef(null);

  // Kullanıcı giriş yaptığında flag'leri sıfırla
  useEffect(() => {
    const handleUserLogin = () => {
      hasLoadedRef.current = false;
      lastUserIdRef.current = null;
    };

    window.addEventListener('notesplus-user-logged-in', handleUserLogin);
    return () => {
      window.removeEventListener('notesplus-user-logged-in', handleUserLogin);
    };
  }, []);

  // Logout olduğunda mola verilerini anında temizle
  useEffect(() => {
    const handleLogout = () => {
      setBreaks([]);
      setSelectedBreak(null);
      hasLoadedRef.current = false;
      lastUserIdRef.current = null;
    };
    window.addEventListener('notesplus-user-logged-out', handleLogout);
    return () => window.removeEventListener('notesplus-user-logged-out', handleLogout);
  }, []);

  // Verileri yükle (sadece ilk yüklemede ve user değiştiğinde)
  useEffect(() => {
    if (!isAuthenticated || !activeToken || !activeUserId) {
      setBreaks([]);
      hasLoadedRef.current = false;
      lastUserIdRef.current = null;
      return;
    }
    
    const currentUserId = activeUserId;
    
    // Eğer aynı kullanıcı ve zaten yüklendiyse, tekrar yükleme
    if (hasLoadedRef.current && lastUserIdRef.current === currentUserId) {
      return;
    }

    const loadBreaks = async () => {
      try {
        const response = await breaksApi.list({ token: activeToken });
        if (response && response.breaks) {
          const normalized = response.breaks.map(normalizeBreakRecord);
          setBreaks(normalized);
          hasLoadedRef.current = true;
          lastUserIdRef.current = currentUserId;
        } else {
          setBreaks([]);
          hasLoadedRef.current = true;
          lastUserIdRef.current = currentUserId;
        }
      } catch (error) {
        console.error('Error loading breaks from API:', error);
        setBreaks([]);
        hasLoadedRef.current = true;
        lastUserIdRef.current = currentUserId;
      }
    };

    loadBreaks();
  }, [activeUserId, isAuthenticated, activeToken]);

  const createBreak = async (breakData) => {
    const now = new Date().toISOString();
    // UUID formatında ID oluştur (PostgreSQL uyumlu)
    const breakId = generateUUID();
    const rawBreak = {
      id: breakId,
      name: breakData.name || i18n.t('breaks:defaults.newBreak'),
      startTime: breakData.startTime || '10:00',
      durationMinutes: breakData.durationMinutes || 15,
      activeDays: breakData.activeDays || {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
      },
      soundEnabled: breakData.soundEnabled !== undefined ? breakData.soundEnabled : true,
      createdAt: now,
      updatedAt: now,
    };

    // ONLINE-FIRST: Önce API'ye kaydet
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        const response = await breaksApi.upsert({
          token: activeToken,
          breaks: [rawBreak],
        });
        
        // API yanıtından gelen veriyi kullan (source of truth)
        if (response?.breaks?.[0] || response?.break) {
          const savedBreak = normalizeBreakRecord(response.breaks?.[0] || response.break);
          setBreaks((prev) => [savedBreak, ...prev]);
          return savedBreak;
        }
      } catch (error) {
        console.error('❌ [BreakReminderContext] Error saving break to API:', error);
        throw error;
      }
    }

    // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
    const normalizedBreak = normalizeBreakRecord(rawBreak);
    setBreaks((prev) => [normalizedBreak, ...prev]);
    return normalizedBreak;
  };

  const updateBreak = async (updatedBreak) => {
    const now = new Date().toISOString();
    const breakPayload = {
      id: updatedBreak.id,
      name: updatedBreak.name || '',
      startTime: updatedBreak.startTime || '10:00',
      durationMinutes: updatedBreak.durationMinutes || 15,
      activeDays: updatedBreak.activeDays || {},
      soundEnabled: updatedBreak.soundEnabled !== undefined ? updatedBreak.soundEnabled : true,
      updatedAt: now,
    };

    // ONLINE-FIRST: Önce API'ye kaydet
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        const response = await breaksApi.upsert({
          token: activeToken,
          breaks: [breakPayload],
        });
        
        // API yanıtından gelen veriyi kullan (source of truth)
        if (response?.breaks?.[0] || response?.break) {
          const savedBreak = normalizeBreakRecord(response.breaks?.[0] || response.break);
          setBreaks((prev) => prev.map((b) => b.id === savedBreak.id ? savedBreak : b));
          if (selectedBreak?.id === savedBreak.id) {
            setSelectedBreak(savedBreak);
          }
          return savedBreak;
        }
      } catch (error) {
        console.error('❌ [BreakReminderContext] Error updating break in API:', error);
        throw error;
      }
    }

    // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
    const normalizedBreak = normalizeBreakRecord({
      ...updatedBreak,
      updatedAt: now,
    });
    setBreaks((prev) => prev.map((b) => b.id === normalizedBreak.id ? normalizedBreak : b));
    if (selectedBreak?.id === normalizedBreak.id) {
      setSelectedBreak(normalizedBreak);
    }
    return normalizedBreak;
  };

  const deleteBreak = async (breakId) => {
    // ONLINE-FIRST: Önce API'den sil
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        await breaksApi.delete({ token: activeToken, breakId });
        // API başarılı olursa local state güncelle
        setBreaks((prev) => prev.filter((breakItem) => breakItem.id !== breakId));
        if (selectedBreak?.id === breakId) {
          setSelectedBreak(null);
        }
      } catch (error) {
        console.error('❌ [BreakReminderContext] Error deleting break from API:', error);
        throw error;
      }
    } else {
      // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
      setBreaks((prev) => prev.filter((breakItem) => breakItem.id !== breakId));
      if (selectedBreak?.id === breakId) {
        setSelectedBreak(null);
      }
    }
  };

  const selectBreak = (breakItem) => {
    setSelectedBreak(breakItem);
  };

  const duplicateBreak = async (breakId) => {
    try {
      const originalBreak = breaks.find((breakItem) => breakItem.id === breakId);
      if (!originalBreak) return null;

      const now = new Date().toISOString();
      // UUID formatında ID oluştur (PostgreSQL uyumlu)
      const newBreakId = generateUUID();
      const rawBreak = {
        id: newBreakId,
        name: originalBreak.name,
        startTime: originalBreak.startTime,
        durationMinutes: originalBreak.durationMinutes,
        activeDays: { ...(originalBreak.activeDays || {}) },
        soundEnabled: originalBreak.soundEnabled,
        createdAt: now,
        updatedAt: now,
      };

      // ONLINE-FIRST: Önce API'ye kaydet
      if (isAuthenticated && activeToken && activeUserId) {
        try {
          const response = await breaksApi.upsert({
            token: activeToken,
            breaks: [rawBreak],
          });
          
          // API yanıtından gelen veriyi kullan (source of truth)
          if (response?.breaks?.[0] || response?.break) {
            const savedBreak = normalizeBreakRecord(response.breaks?.[0] || response.break);
            setBreaks((prev) => [savedBreak, ...prev]);
            return savedBreak;
          }
        } catch (error) {
          console.error('❌ [BreakReminderContext] Error duplicating break in API:', error);
          throw error;
        }
      }

      // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
      const duplicatedBreak = normalizeBreakRecord(rawBreak);
      setBreaks((prev) => [duplicatedBreak, ...prev]);
      return duplicatedBreak;
    } catch (error) {
      console.error('Error duplicating break reminder:', error);
      alert(i18n.t('shared:breakReminderContext.duplicateFailedAlert', { reason: getApiErrorMessage(error, { fallback: i18n.t('shared:cloudApiErrors.unknownError') }) }));
      return null;
    }
  };

  const clearAllBreakReminders = async () => {
    try {
      // API'den tüm verileri sil
      if (isAuthenticated && activeToken && activeUserId) {
        for (const breakItem of breaks) {
          try {
            await breaksApi.delete({ token: activeToken, breakId: breakItem.id });
          } catch (error) {
            console.error('Mola hatırlatıcısı silme hatası:', error);
          }
        }
      }

      setBreaks([]);
      setSelectedBreak(null);
    } catch (error) {
      console.error('Mola hatırlatıcıları temizlenirken hata:', error);
      throw error;
    }
  };

  // API'den molaları yeniden yükle (import sonrası anlık senkron için)
  const reloadBreaks = useCallback(async () => {
    if (!isAuthenticated || !activeToken || !activeUserId) return;
    try {
      const response = await breaksApi.list({ token: activeToken });
      if (response?.breaks) {
        const normalized = response.breaks.map(normalizeBreakRecord);
        setBreaks(normalized);
      } else {
        setBreaks([]);
      }
    } catch (error) {
      console.error('❌ [BreakReminderContext] reloadBreaks failed:', error);
      setBreaks([]);
    }
  }, [isAuthenticated, activeToken, activeUserId]);

  const value = {
    breaks,
    selectedBreak,
    createBreak,
    updateBreak,
    deleteBreak,
    selectBreak,
    duplicateBreak,
    clearAllBreakReminders,
    reloadBreaks,
  };

  return <BreakReminderContext.Provider value={value}>{children}</BreakReminderContext.Provider>;
};

export const useBreakReminder = () => {
  const context = useContext(BreakReminderContext);
  if (!context) {
    throw new Error('useBreakReminder must be used within a BreakReminderProvider');
  }
  return context;
};




